/**
 * Configuration Socket.IO pour le chat en temps réel
 */

import { io, Socket } from 'socket.io-client'
import { TokenStorage } from '@/services/apiConfig'

// Configuration depuis les variables d'environnement
const getEnvVar = (key: string, defaultValue: string): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (process.env as any)[key] || defaultValue
}

// URL de base du serveur Socket.IO (sans le path)
const getSocketBaseUrl = (): string => {
  // Si une URL complète est fournie, retirer le path
  const socketUrl = getEnvVar('VUE_APP_SOCKET_URL', '')
  if (socketUrl) {
    try {
      const url = new URL(socketUrl)
      // Retourner seulement protocol + hostname + port (sans path)
      return `${url.protocol}//${url.hostname}${url.port ? ':' + url.port : ''}`
    } catch {
      // Si l'URL est invalide, retirer manuellement le path
      return socketUrl.replace(/\/socket\.io.*$/i, '').replace(/\/$/, '')
    }
  }
  
  // Sinon, construire l'URL depuis l'API
  const apiUrl = getEnvVar('VUE_APP_API_URL', 'https://backend.coop-finance-app.wixoon.dev/api')
  // Retirer /api de la fin si présent
  const baseUrl = apiUrl.replace(/\/api$/, '')
  const socketPort = getEnvVar('VUE_APP_SOCKET_PORT', '3000')
  
  // Pour le développement local, utiliser localhost directement
  if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1') || baseUrl.includes('localhost:8000')) {
    return `http://localhost:${socketPort}`
  }
  
  // En production, utiliser l'URL du domaine principal (sans port)
  // Nginx proxifiera /socket.io/ vers le serveur Socket.IO local
  try {
    const url = new URL(baseUrl)
    // Ne pas ajouter le port en production - laisser Nginx gérer le routage
    return `${url.protocol}//${url.hostname}`
  } catch (error) {
    // Fallback si l'URL est invalide
    console.warn('[Socket.IO] ⚠️ URL invalide, utilisation de localhost:', error)
    return `http://localhost:${socketPort}`
  }
}

const socketBaseUrl = getSocketBaseUrl()

// Créer l'instance Socket.IO avec gestion d'erreur
let socket: Socket | null = null

/**
 * Initialiser la connexion Socket.IO
 */
export const initSocket = (): Socket | null => {
  if (socket?.connected) {
    return socket
  }

  try {
    const token = TokenStorage.getToken()
    const socketPath = getEnvVar('VUE_APP_SOCKET_PATH', '/socket.io')
    
    console.log('[Socket.IO] 🔌 Connexion à:', socketBaseUrl, 'avec path:', socketPath)
    
    socket = io(socketBaseUrl, {
      path: socketPath,
      auth: {
        token: token || ''
      },
      transports: ['websocket', 'polling'], // Essayer WebSocket d'abord, puis polling en fallback
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000
    })

    // Gérer les événements de connexion
    socket.on('connect', () => {
      console.log('[Socket.IO] ✅ Connexion établie avec succès')
      // Mettre à jour le token d'authentification si nécessaire
      if (token) {
        socket?.emit('authenticate', { token })
      }
    })

    socket.on('disconnect', (reason) => {
      console.warn('[Socket.IO] ⚠️ Déconnexion:', reason)
    })

    socket.on('connect_error', (error) => {
      console.error('[Socket.IO] ❌ Erreur de connexion:', error.message)
    })

    socket.on('reconnect', (attemptNumber) => {
      console.log('[Socket.IO] 🔄 Reconnexion réussie après', attemptNumber, 'tentative(s)')
      // Ré-authentifier après reconnexion
      const currentToken = TokenStorage.getToken()
      if (currentToken) {
        socket?.emit('authenticate', { token: currentToken })
      }
    })

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('[Socket.IO] 🔄 Tentative de reconnexion', attemptNumber)
    })

    socket.on('reconnect_failed', () => {
      console.error('[Socket.IO] ❌ Échec de la reconnexion après toutes les tentatives')
    })

    return socket
  } catch (error) {
    console.warn('[Socket.IO] ⚠️ Impossible d\'initialiser Socket.IO. Le temps réel ne fonctionnera pas:', error)
    return null
  }
}

/**
 * Obtenir l'instance Socket.IO (l'initialiser si nécessaire)
 */
export const getSocket = (): Socket | null => {
  if (!socket || !socket.connected) {
    return initSocket()
  }
  return socket
}

/**
 * Mettre à jour l'authentification Socket.IO avec un nouveau token
 */
export const updateSocketAuth = (token: string | null): void => {
  if (socket && socket.connected) {
    socket.auth = { token: token || '' }
    socket.emit('authenticate', { token: token || '' })
  } else if (token) {
    // Si le socket n'est pas connecté mais qu'on a un token, réinitialiser
    initSocket()
  }
}

/**
 * Déconnecter le socket
 */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

/**
 * Vérifier si Socket.IO est connecté
 */
export const isSocketConnected = (): boolean => {
  return socket?.connected || false
}

/**
 * S'abonner à un canal (room) pour écouter les messages
 */
export const joinRoom = (roomName: string, callback?: (data: any) => void): void => {
  const currentSocket = getSocket()
  if (!currentSocket) {
    console.warn('[Socket.IO] ⚠️ Impossible de rejoindre le canal, socket non disponible')
    return
  }

  // Émettre l'événement 'join' pour rejoindre le room côté serveur
  currentSocket.emit('join', roomName)
  console.log('[Socket.IO] 📥 Rejoint le room:', roomName)
  
  if (callback) {
    // Écouter les messages du canal avec le format: roomName:message ou new.message
    // Le serveur peut envoyer les messages avec l'événement 'new.message' ou 'message'
    currentSocket.on(`message:${roomName}`, callback)
    // Écouter aussi l'événement global 'new.message' qui peut contenir le roomName dans les données
    currentSocket.on('new.message', (data: any) => {
      // Vérifier si le message appartient à ce room
      if (data.conversation_id && roomName.includes(String(data.conversation_id))) {
        callback(data)
      }
    })
  }
}

/**
 * Quitter un canal (room)
 */
export const leaveRoom = (roomName: string): void => {
  if (!socket) return
  
  socket.emit('leave', roomName)
  console.log('[Socket.IO] 📤 Quitté le room:', roomName)
  // Retirer tous les listeners pour ce canal
  socket.off(`message:${roomName}`)
}

/**
 * Écouter un événement spécifique
 */
export const onEvent = (eventName: string, callback: (data: any) => void): void => {
  const currentSocket = getSocket()
  if (!currentSocket) {
    console.warn('[Socket.IO] ⚠️ Impossible d\'écouter l\'événement, socket non disponible')
    return
  }
  
  console.log(`[Socket.IO] 📝 Enregistrement de l'événement "${eventName}" sur le socket:`, {
    socketId: currentSocket.id,
    connected: currentSocket.connected
  })
  
  currentSocket.on(eventName, callback)
  
  console.log(`[Socket.IO] ✅ Événement "${eventName}" enregistré avec succès`)
}

/**
 * Arrêter d'écouter un événement
 */
export const offEvent = (eventName: string, callback?: (data: any) => void): void => {
  if (!socket) return
  
  if (callback) {
    socket.off(eventName, callback)
  } else {
    socket.off(eventName)
  }
}

export default socket
